import { useEffect, useState } from "react";
import { useGetIdentity, useNotify, useUpdate } from "ra-core";
import { CalendarClock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { Task } from "../types";

const localValue = (value?: string | null) => {
  if (!value) return "";
  const date = new Date(value);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
};
export const TaskSchedule = ({
  task,
  onSaved,
}: {
  task: Task;
  onSaved?: () => void | Promise<void>;
}) => {
  const { identity } = useGetIdentity();
  const notify = useNotify();
  const [update, { isPending }] = useUpdate();
  const [start, setStart] = useState(localValue(task.scheduled_start));
  const [end, setEnd] = useState(localValue(task.scheduled_end));
  useEffect(() => {
    setStart(localValue(task.scheduled_start));
    setEnd(localValue(task.scheduled_end));
  }, [task.scheduled_start, task.scheduled_end]);
  const canEdit =
    !!identity &&
    (identity.administrator ||
      String(task.sales_id) === String(identity.id) ||
      String(task.created_by_sales_id) === String(identity.id));
  const save = () => {
    const first = new Date(start),
      last = new Date(end);
    if (
      !start ||
      !end ||
      !Number.isFinite(first.getTime()) ||
      !Number.isFinite(last.getTime()) ||
      last <= first
    ) {
      notify("Укажите начало и окончание: окончание должно быть позже начала", {
        type: "warning",
      });
      return;
    }
    update(
      "tasks",
      {
        id: task.id,
        previousData: task,
        data: {
          scheduled_start: first.toISOString(),
          scheduled_end: last.toISOString(),
        },
      },
      {
        mutationMode: "pessimistic",
        onSuccess: async () => {
          await onSaved?.();
          notify("Период задачи обновлено", { type: "success" });
        },
        onError: () => notify("Не удалось изменить время", { type: "error" }),
      },
    );
  };
  return (
    <section className="space-y-3">
      <h3 className="flex items-center gap-2 text-sm font-medium">
        <CalendarClock className="size-4" />
        Время в календаре
      </h3>
      <p className="text-xs text-muted-foreground">
        Задача видна каждый день от начала до окончания. Окончание — дедлайн.
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="space-y-1 text-xs text-muted-foreground">
          Начало
          <Input
            aria-label="Начало работы"
            type="datetime-local"
            value={start}
            required
            disabled={!canEdit}
            onChange={(e) => setStart(e.target.value)}
          />
        </label>
        <label className="space-y-1 text-xs text-muted-foreground">
          Окончание
          <Input
            aria-label="Окончание работы"
            type="datetime-local"
            value={end}
            required
            disabled={!canEdit}
            onChange={(e) => setEnd(e.target.value)}
          />
        </label>
      </div>
      {canEdit && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={isPending}
          onClick={save}
        >
          Сохранить время
        </Button>
      )}
    </section>
  );
};
